import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { toggleMark, setBlockType, wrapIn, joinUp, lift } from 'prosemirror-commands';
import { wrapInList } from 'prosemirror-schema-list';
import { undo, redo } from 'prosemirror-history';
import _compact from 'lodash/compact';
import { FONT_FAMILY_OPTIONS, HEADING_LEVEL_FONT_SIZES, MENU_GROUP_TYPES } from './constants';
import { markActive, setAlignment, setFontFamily, toggleLinkItem } from './menu.actions';
function linkItem(markType) {
  return {
    id: 'add-link',
    title: 'Add or remove link',
    type: 'icon',
    icon: 'tap-icon-editor-link',
    isEnabled: state => !state.selection.empty,
    run: toggleLinkItem(markType)
  };
}
export const getIconItem = (id, icon, title, run, otherOptions) => _objectSpread({
  type: 'icon',
  id,
  icon,
  title,
  run
}, otherOptions || {});
export const getColorPalette = (id, icon, title, run, otherOptions) => _objectSpread({
  type: 'colorPalette',
  id,
  icon,
  title,
  run
}, otherOptions || {});
export const getLabelItem = (id, label, title, run, otherOptions) => _objectSpread({
  type: 'label',
  id,
  label,
  title,
  run
}, otherOptions || {});
export const getDropdownItem = (id, label, items, icon, otherOptions) => _objectSpread({
  type: 'dropdown',
  id,
  label,
  items,
  icon
}, otherOptions || {});
export const getSeparator = id => ({
  type: 'separator',
  id
});

/* Menu Items */
export const SEPERATOR_ITEM = Object.freeze({
  type: 'separator'
});
const undoItem = getIconItem('toggle-undo', 'tap-icon-editor-left-arrow', __('Undo last change'), undo);
const redoItem = getIconItem('toggle-redo', 'tap-icon-editor-right-arrow', __('Redo last undone change'), redo);
const getFontFamilyItem = schema => {
  const items = [];
  if (!schema.marks.fontFamily) return null;
  FONT_FAMILY_OPTIONS.forEach(option => {
    items.push(getLabelItem(option.key, option.label, "".concat(__('Change to font family'), " ").concat(option.label), (state, dispatch, view) => setFontFamily(state, dispatch, view, schema.marks.fontFamily, option.key), {
      style: {
        fontFamily: option.key
      }
    }));
  });
  return getDropdownItem('fontFamily', __('Font family'), items, '');
};
const getHeadingItem = schema => {
  if (!schema.nodes.heading) return null;
  const items = [];
  HEADING_LEVEL_FONT_SIZES.forEach((size, index) => {
    items.push(getLabelItem("heading-".concat(index + 1), "".concat(__('Level {{level}}', {
      level: index + 1
    })), "".concat(__('Change to heading'), " ").concat(index + 1), setBlockType(schema.nodes.heading, {
      level: index + 1
    }), {
      style: {
        fontSize: size
      }
    }));
  });
  return getDropdownItem('heading', __('Headings'), items, '');
};
const getBoldItem = schema => {
  return schema.marks.strong && getIconItem('toggle-strong', 'tap-icon-editor-bold', __('Bold'), toggleMark(schema.marks.strong), {
    isActive: state => markActive(state, schema.marks.strong)
  });
};
const getItalicItem = schema => {
  return schema.marks.em && getIconItem('toggle-emphasis', 'tap-icon-editor-italic', __('Italic'), toggleMark(schema.marks.em), {
    isActive: state => markActive(state, schema.marks.em)
  });
};
const getStrikeThroughItem = schema => {
  return schema.marks.strikeThrough && getLabelItem('toggle-strikeThrough', __('Strike through'), __('Toggle strike through'), toggleMark(schema.marks.strikeThrough), {
    isActive: state => markActive(state, schema.marks.strikeThrough),
    className: 'icon-strikethrough'
  });
};
const getUnderlineItem = schema => {
  return schema.marks.underline && getLabelItem('toggle-underline', __('Underline'), __('Toggle underline'), toggleMark(schema.marks.underline), {
    isActive: state => markActive(state, schema.marks.underline),
    className: 'icon-format-underlined'
  });
};
const getTextColorItem = schema => {
  return schema.marks.textColor && getColorPalette('toggle-textColor', 'tap-icon-editor-text-color', __('Toggle text color'), null, {
    isActive: state => markActive(state, schema.marks.textColor),
    markType: schema.marks.textColor,
    tooltipProps: {
      placement: 'top'
    }
  });
};
const getTextHighlightItem = schema => {
  return schema.marks.textHighlight && getColorPalette('toggle-textHighlight', 'tap-icon-editor-text-highlighter', __('Toggle text highlight'), null, {
    isActive: state => markActive(state, schema.marks.textHighlight),
    markType: schema.marks.textHighlight,
    tooltipProps: {
      placement: 'top'
    }
  });
};

/* Menu Groups */
const groupUndoRedo = (_, editorProps) => editorProps.showUndoRedoMenuItem && {
  id: MENU_GROUP_TYPES.UNDO_REDO,
  type: 'group',
  items: _compact([undoItem, redoItem])
};
const groupFontFamilyHeading = schema => {
  const items = _compact([getFontFamilyItem(schema), getHeadingItem(schema)]);
  if (!items.length) return null;
  return {
    id: MENU_GROUP_TYPES.FONTS,
    type: 'group',
    items: items
  };
};
const groupBoldItalicItems = schema => ({
  id: MENU_GROUP_TYPES.TEXT_STYLES,
  type: 'group',
  hasMore: false,
  maxItems: 2,
  items: _compact([getBoldItem(schema), getItalicItem(schema), getStrikeThroughItem(schema), getUnderlineItem(schema)])
});
const groupTextFormats = schema => ({
  id: MENU_GROUP_TYPES.TEXT_HIGHLIGHTS,
  type: 'group',
  items: _compact([getTextColorItem(schema), schema.marks.link && linkItem(schema.marks.link), getTextHighlightItem(schema)])
});
const groupTextAlignmentsLists = schema => {
  const alignmentProps = {
    tooltipProps: {
      placement: 'bottom'
    }
  };
  return {
    id: MENU_GROUP_TYPES.TEXT_ALIGNMENTS,
    type: 'group',
    items: _compact([/* Alignment Items */
    schema.marks.textAlignment && getDropdownItem('align-menu', __('Text alignments'), [getIconItem('toggle-alignLeft', 'tap-icon-editor-align-left', __('Align left'), (state, dispatch) => setAlignment(state, dispatch, schema.marks.textAlignment, 'left'), alignmentProps), getIconItem('toggle-alignCenter', 'tap-icon-editor-align-center', __('Align center'), (state, dispatch) => setAlignment(state, dispatch, schema.marks.textAlignment, 'center'), alignmentProps), getIconItem('toggle-alignRight', 'tap-icon-editor-align-right', __('Align right'), (state, dispatch) => setAlignment(state, dispatch, schema.marks.textAlignment, 'right'), alignmentProps), getIconItem('toggle-alignJustify', 'tap-icon-editor-align-center', __('Justify content'), (state, dispatch) => setAlignment(state, dispatch, schema.marks.textAlignment, 'justify'), alignmentProps)], 'tap-icon-editor-align-left'), schema.nodes.ordered_list && getIconItem('toggle-orderedlist', 'tap-icon-editor-order-list', __('Ordered list'), wrapInList(schema.nodes.ordered_list)), schema.nodes.bullet_list && getIconItem('toggle-bulletlist', 'tap-icon-editor-unorder-list', __('Bullet list'), wrapInList(schema.nodes.bullet_list)), getIconItem('toggle-joinup', 'tap-icon-editor-join', __('Join with above block'), joinUp, {
      isHidden: state => !joinUp(state)
    }), getIconItem('toggle-lift-listitem', 'tap-icon-editor-lift', __('Lift out of enclosing block'), lift, {
      isHidden: state => !lift(state)
    })])
  };
};
const groupInsertBlocks = schema => ({
  id: MENU_GROUP_TYPES.INSERT,
  type: 'group',
  items: _compact([schema.nodes.code_block && getIconItem('toggle-codeicon', 'tap-icon-editor-code', __('Change to code block'), setBlockType(schema.nodes.code_block)), schema.nodes.paragraph && getLabelItem('toggle-paragraph', __('Text'), __('Change to paragraph'), setBlockType(schema.nodes.paragraph)), schema.nodes.blockquote && getIconItem('toggle-blockquote', 'tap-icon-editor-quote', __('Wrap in block quote'), wrapIn(schema.nodes.blockquote))])
});
export const groupCustomBlocks = () => ({
  id: MENU_GROUP_TYPES.CUSTOM,
  type: 'group',
  hideSeperatorAtEnd: true,
  items: []
});
export const buildMenuItems = (schema, editorProps) => [groupUndoRedo(schema, editorProps), groupFontFamilyHeading(schema, editorProps), groupBoldItalicItems(schema, editorProps), groupTextFormats(schema, editorProps), groupTextAlignmentsLists(schema, editorProps), groupInsertBlocks(schema, editorProps)].filter(group => {
  var _group$items;
  return group && ((_group$items = group.items) === null || _group$items === void 0 ? void 0 : _group$items.length) > 0;
});